/**
 * Runtime Configuration Module
 *
 * Provides runtime configuration values that can be injected at container
 * startup without requiring a rebuild. This enables the same Docker image
 * to be deployed to different environments (dev, staging, prod) with
 * environment-specific configuration.
 *
 * Configuration sources (in order of precedence):
 * 1. globalThis.__CONFIG__ - Runtime injection via config.js (production)
 * 2. import.meta.env - Vite environment variables (development)
 * 3. Default values - Fallback for local development
 */

/**
 * Runtime configuration interface.
 * Contains only the values that need to be configurable per environment.
 */
export interface RuntimeConfig {
  readonly BACKEND_BASE_URL: string
  readonly KEYCLOAK_BASE_URL: string
  readonly KEYCLOAK_REALM: string
  readonly KEYCLOAK_CLIENT_ID: string
  readonly ENABLE_DEVTOOLS: boolean
  readonly ENABLE_MSW: boolean
}

/**
 * Parses a string value to boolean.
 * Returns the default value if the input is undefined or empty.
 */
const parseBoolean = (value: string | boolean | undefined, defaultValue: boolean): boolean => {
  if (value === undefined || value === '') return defaultValue
  if (typeof value === 'boolean') return value
  return value.toLowerCase() === 'true'
}

/**
 * Validates that required configuration fields are present and non-empty.
 * Throws an error if any required fields are missing.
 */
function validateConfig(config: Partial<RuntimeConfig>): void {
  const required: (keyof RuntimeConfig)[] = [
    'BACKEND_BASE_URL',
    'KEYCLOAK_BASE_URL',
    'KEYCLOAK_REALM',
    'KEYCLOAK_CLIENT_ID',
  ]

  const missing = required.filter((key) => !config[key])

  if (missing.length > 0) {
    throw new Error(`Missing required configuration: ${missing.join(', ')}`)
  }
}

/**
 * Retrieves the runtime configuration.
 *
 * Configuration resolution order:
 * 1. globalThis.__CONFIG__ (runtime injection from config.js) - Production
 * 2. import.meta.env.VITE_* (Vite build-time variables) - Development only
 *
 * @returns The resolved runtime configuration
 * @throws Error if not in DEV mode and globalThis.__CONFIG__ is not available
 */
export function getRuntimeConfig(): RuntimeConfig {
  // Production: Use globalThis.__CONFIG__
  if (typeof globalThis !== 'undefined' && globalThis.__CONFIG__) {
    const config: RuntimeConfig = {
      BACKEND_BASE_URL: globalThis.__CONFIG__.BACKEND_BASE_URL,
      KEYCLOAK_BASE_URL: globalThis.__CONFIG__.KEYCLOAK_BASE_URL,
      KEYCLOAK_REALM: globalThis.__CONFIG__.KEYCLOAK_REALM,
      KEYCLOAK_CLIENT_ID: globalThis.__CONFIG__.KEYCLOAK_CLIENT_ID,
      ENABLE_DEVTOOLS: parseBoolean(globalThis.__CONFIG__.ENABLE_DEVTOOLS, false),
      ENABLE_MSW: parseBoolean(globalThis.__CONFIG__.ENABLE_MSW, false),
    }

    validateConfig(config)
    return config
  }

  // Development: Fallback to import.meta.env
  if (import.meta.env.DEV) {
    const config: RuntimeConfig = {
      BACKEND_BASE_URL: import.meta.env.VITE_BACKEND_BASE_URL || 'http://localhost:8086',
      KEYCLOAK_BASE_URL: import.meta.env.VITE_KEYCLOAK_BASE_URL || 'http://localhost:8080',
      KEYCLOAK_REALM: import.meta.env.VITE_KEYCLOAK_REALM || 'claude-code-hooks',
      KEYCLOAK_CLIENT_ID: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'claude-code-hooks-frontend',
      ENABLE_DEVTOOLS: parseBoolean(import.meta.env.VITE_ENABLE_DEVTOOLS, true),
      ENABLE_MSW: parseBoolean(import.meta.env.VITE_ENABLE_MSW, false),
    }

    return config
  }

  throw new Error(
    'Runtime configuration not loaded. ' +
      'Ensure config.js is loaded in index.html before the application bundle.',
  )
}
